import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { resolveImageUrl } from "@/lib/api";
import { useFeatureFlag } from "@/lib/featureFlags";
import NewsletterForm from "@/components/NewsletterForm";

const SOCIAL_ICONS = {
    facebook: Facebook,
    instagram: Instagram,
    twitter: Twitter,
    youtube: Youtube,
};

// Inline SVG payment-method logos (royalty-free, simplified, brand-recognizable)
const VisaLogo = () => (
    <svg viewBox="0 0 64 24" className="h-5 w-auto" aria-label="Visa">
        <rect width="64" height="24" rx="3" fill="#1A1F71" />
        <text x="32" y="17" textAnchor="middle" fill="#fff" fontFamily="Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="13" letterSpacing="1">VISA</text>
    </svg>
);
const MastercardLogo = () => (
    <svg viewBox="0 0 64 24" className="h-5 w-auto" aria-label="Mastercard">
        <rect width="64" height="24" rx="3" fill="#fff" />
        <circle cx="26" cy="12" r="7" fill="#EB001B" />
        <circle cx="38" cy="12" r="7" fill="#F79E1B" />
        <path d="M32 6.5a7 7 0 010 11 7 7 0 010-11z" fill="#FF5F00" />
    </svg>
);
const TroyLogo = () => (
    <svg viewBox="0 0 64 24" className="h-5 w-auto" aria-label="Troy">
        <rect width="64" height="24" rx="3" fill="#fff" />
        <text x="32" y="17" textAnchor="middle" fill="#1A2B5F" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="14" letterSpacing="0.5">troy</text>
        <circle cx="51" cy="9" r="1.8" fill="#E30613" />
    </svg>
);

function SocialLink({ kind, url }) {
    if (!url) return null;
    const Icon = SOCIAL_ICONS[kind];
    return (
        <a data-testid={`social-link-${kind}`} href={url} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            {Icon ? <Icon className="h-4 w-4 text-white" /> : <span className="text-white text-xs font-bold uppercase">{kind[0]}</span>}
        </a>
    );
}

export default function Footer() {
    const site = useSiteContent();
    const showNewsletter = useFeatureFlag("show_newsletter_footer", true);
    const f = site?.footer || {};
    const brand = site?.brand || {};
    const columns = f.columns || [];
    const social = f.social || {};
    const contact = f.contact || {};
    const certs = f.certifications || {};
    const payment = f.payment_methods || {};
    const showCardLogos = payment.show_card_logos !== false; // default true
    const hasCerts = certs.etbis_logo_url || certs.etbis_number || certs.tursab_logo_url || certs.tursab_number;

    return (
        <footer data-testid="site-footer" className="bg-slate-900 text-slate-300 mt-16">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl py-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    {/* Brand column */}
                    <div className="md:col-span-4 space-y-3">
                        <div className="font-display text-2xl font-bold text-white">{brand.name || "Msonline Travel"}</div>
                        {brand.tagline && <p className="text-sm text-slate-400">{brand.tagline}</p>}
                        <div className="space-y-1.5 pt-2 text-sm">
                            {contact.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-sky-400" /><a href={`tel:${contact.phone}`} className="hover:text-white transition-colors">{contact.phone}</a></div>}
                            {contact.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-sky-400" /><a href={`mailto:${contact.email}`} className="hover:text-white transition-colors">{contact.email}</a></div>}
                            {contact.address && <div className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-sky-400 mt-0.5" /><span>{contact.address}</span></div>}
                        </div>
                        <div className="flex gap-2 pt-3">
                            <SocialLink kind="facebook" url={social.facebook} />
                            <SocialLink kind="instagram" url={social.instagram} />
                            <SocialLink kind="twitter" url={social.twitter} />
                            <SocialLink kind="youtube" url={social.youtube} />
                            {social.tiktok && <a data-testid="social-link-tiktok" href={social.tiktok} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xs font-bold">TT</a>}
                            {social.whatsapp && <a data-testid="social-link-whatsapp" href={social.whatsapp} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 flex items-center justify-center text-emerald-400 text-xs font-bold">WA</a>}
                        </div>
                    </div>

                    {/* Dynamic columns */}
                    {columns.map((col) => (
                        <div key={col.id} className={`md:col-span-${Math.max(2, Math.floor(8 / Math.max(1, columns.length)))} space-y-3`}>
                            <h3 className="font-display font-bold text-white text-sm uppercase tracking-widest">{col.title}</h3>
                            <ul className="space-y-2 text-sm">
                                {(col.links || []).map((l) => (
                                    <li key={l.id}>
                                        {l.url?.startsWith("http") ? (
                                            <a href={l.url} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{l.label}</a>
                                        ) : (
                                            <Link to={l.url || "#"} className="hover:text-white transition-colors">{l.label}</Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Newsletter strip (Faz 5) — controlled by feature flag */}
                {showNewsletter && (
                <div className="mt-10 pt-8 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-6 items-center" data-testid="footer-newsletter">
                    <div>
                        <h3 className="font-display font-bold text-white text-base">Kampanyalardan ilk siz haberdar olun</h3>
                        <p className="text-xs text-slate-400 mt-1">Sezon indirimleri, fırsat otelleri ve özel kuponlar e-postanızda.</p>
                    </div>
                    <NewsletterForm source="footer" variant="inline" />
                </div>
                )}

                {/* Certifications + Payment Methods band */}
                {(hasCerts || showCardLogos) && (
                    <div data-testid="footer-cert-band" className="mt-10 pt-8 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        {/* Certifications (ETBİS / TÜRSAB) */}
                        <div className="flex flex-wrap items-center gap-4">
                            {(certs.etbis_logo_url || certs.etbis_number) && (
                                <a
                                    data-testid="footer-etbis-badge"
                                    href={certs.etbis_link || "https://www.eticaret.gov.tr/siteprofil/" + encodeURIComponent(certs.etbis_number || "")}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-xl px-3 py-2 border border-white/10 transition-colors"
                                >
                                    {certs.etbis_logo_url && <img src={resolveImageUrl(certs.etbis_logo_url)} alt="ETBİS" className="h-10 w-auto max-w-[80px] object-contain" />}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">ETBİS</div>
                                        {certs.etbis_number && <div className="text-xs font-mono text-slate-200">{certs.etbis_number}</div>}
                                    </div>
                                </a>
                            )}
                            {(certs.tursab_logo_url || certs.tursab_number) && (
                                <a
                                    data-testid="footer-tursab-badge"
                                    href={certs.tursab_link || "https://www.tursab.org.tr/"}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-xl px-3 py-2 border border-white/10 transition-colors"
                                >
                                    {certs.tursab_logo_url && <img src={resolveImageUrl(certs.tursab_logo_url)} alt="TÜRSAB" className="h-10 w-auto max-w-[80px] object-contain" />}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">TÜRSAB</div>
                                        {certs.tursab_number && <div className="text-xs font-mono text-slate-200">{certs.tursab_number}</div>}
                                    </div>
                                </a>
                            )}
                        </div>

                        {/* Payment method logos */}
                        {showCardLogos && (
                            <div data-testid="footer-payment-logos" className="flex flex-wrap items-center gap-2 md:justify-end">
                                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mr-2">Güvenli Ödeme</span>
                                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10"><VisaLogo /></div>
                                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10"><MastercardLogo /></div>
                                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10"><TroyLogo /></div>
                                {(payment.extra_logos || []).map((logoUrl, i) => (
                                    <div key={i} className="flex items-center bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10">
                                        <img src={resolveImageUrl(logoUrl)} alt="Payment method" className="h-5 w-auto object-contain" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Bottom bar */}
                <div className="mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
                    <div>{f.copyright || `© ${new Date().getFullYear()} Msonline Travel. Tüm hakları saklıdır.`}</div>
                    <div className="flex items-center gap-3">
                        <Link to="/page/kvkk" className="hover:text-white">KVKK</Link>
                        <Link to="/page/gizlilik" className="hover:text-white">Gizlilik</Link>
                        <Link to="/page/kosullar" className="hover:text-white">Şartlar</Link>
                        <Link to="/basvuru-takip" className="hover:text-white" data-testid="footer-application-track">Başvuru Takip</Link>
                        <span className="text-slate-700">·</span>
                        <Link to="/staff-login" className="text-slate-600 hover:text-slate-400 transition-colors" data-testid="footer-staff-login">Personel Girişi</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
